import React, { useState } from "react";
import Layout from "./Layout";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { useItemsFilter } from "../../../_helper/actions/app/items";
import { ApiEndPoint } from "../../../_helper/constant/api-types";
import { useAtom } from "jotai";
import { pharmacyAtom } from "../../../_helper/constant/constant-atom";
import { formatDate } from "../../../_helper/utils/utils";

const MedicineScheduleDashboard = () => {
  // Set default date range: last 15 days (start) to today (end)
  const today = new Date();
  const defaultEndDate = today.toISOString().split("T")[0];
  const last15Days = new Date(today);
  last15Days.setDate(today.getDate() - 15);
  const defaultStartDate = last15Days.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [pharmacy] = useAtom(pharmacyAtom);

  const COLORS = ["#4CAF50", "#2196F3", "#FFC107", "#FF5722"];

  // Fetch compliance record data (used for both bar and pie charts)
  const { data, isLoading: isComplianceRecordLoading } = useItemsFilter(
    ApiEndPoint.PHARMACY_STATISTICS,
    `?action=getComplianceRecord&date_created_after=${startDate}&date_created_before=${endDate}&pharmacy_id=${pharmacy?.id}`
  );

  // Fetch medicine tracking data for the table
  const {
    data: scheduleMedicneTracking,
    isLoading: isScheduleMedicneTrackingLoading,
  } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getTagSchedulePharmacyBillTracking&date_created_after=${startDate}&date_created_before=${endDate}&pharmacy_id=${pharmacy?.id}`
  );

  return (
    <Layout>
      {/* Header Section */}
      <div className="mb-8">
        <p className="text-gray-600">
          Manage all aspects of your medicine schedules, including tagging,
          compliance tracking, reporting, and analytics.
        </p>
      </div>

      {/* Date Range Filter Section */}
      <div className="flex items-center gap-4 mb-6">
        <div>
          <label className="block text-gray-600 font-semibold">
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-300 rounded p-2"
          />
        </div>
        <div>
          <label className="block text-gray-600 font-semibold">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-300 rounded p-2"
          />
        </div>
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Bar Chart for Compliance Records */}
        <div className="p-6 bg-white shadow rounded-lg">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Compliance Records
          </h3>
          {isComplianceRecordLoading ? (
            <div className="flex items-center justify-center h-72">
              <p className="text-gray-500">Loading compliance records...</p>
            </div>
          ) : data && data.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="schedule" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#4CAF50" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-72">
              <p className="text-gray-500">No compliance records available</p>
            </div>
          )}
        </div>

        {/* Pie Chart for Tagged Schedules */}
        <div className="p-6 bg-white shadow rounded-lg">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Tagged Schedules
          </h3>
          {isComplianceRecordLoading ? (
            <div className="flex items-center justify-center h-72">
              <p className="text-gray-500">Loading tagged schedules...</p>
            </div>
          ) : data && data.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="count"
                  nameKey="schedule"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {data?.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS?.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-72">
              <p className="text-gray-500">No tagged schedules available</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Medicines Section */}
      <div className="p-6 bg-white shadow rounded-lg">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Medicines</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">#</th>
                <th className="px-4 py-2 text-left">Medicine</th>
                <th className="px-4 py-2 text-left">Patient</th>
                <th className="px-4 py-2 text-left">Schedule</th>
                <th className="px-4 py-2 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {isScheduleMedicneTrackingLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    Loading medicines...
                  </td>
                </tr>
              ) : scheduleMedicneTracking &&
                scheduleMedicneTracking.length > 0 ? (
                scheduleMedicneTracking?.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-gray-100`}
                  >
                    <td className="px-4 py-2">{index + 1}</td>
                    <td className="px-4 py-2">{item?.drug_name}</td>
                    <td className="px-4 py-2">{item?.patient_id?.full_name}</td>
                    <td className="px-4 py-2">{item?.schedule}</td>
                    <td className="px-4 py-2">
                      {formatDate(item?.date_created)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    No recent medicines available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default MedicineScheduleDashboard;
